// =============================================================================
// Host-app pluggable error reporting. sharedComponents is consumed by several
// apps and must not depend on a specific telemetry SDK, so caught errors are
// routed through a reporter the host registers at bootstrap (play-area wires
// Sentry in its index.tsx). Console output always happens; the reporter is
// additive, and a missing reporter degrades to today's console-only behavior.
// =============================================================================

export type SharedErrorLevel = 'error' | 'warning'

export type SharedErrorTags = Record<string, string | number | boolean>

export type SharedErrorReporter = (
    message: string,
    error: unknown,
    context?: Record<string, unknown>,
    level?: SharedErrorLevel,
    tags?: SharedErrorTags,
) => void

let reporter: SharedErrorReporter | undefined

export const setSharedErrorReporter = (nextReporter: SharedErrorReporter): void => {
    reporter = nextReporter
}

// Console-capture tooling (e.g. Sentry breadcrumbs) stringifies each console
// argument with String(), turning plain objects into "[object Object]".
// Errors stringify readably ("Error: message") and keep their inspectable
// stack in devtools, so they pass through as-is; everything else goes as JSON.
const printable = (value: unknown): unknown => {
    if (value instanceof Error) return value
    try {
        const json = JSON.stringify(value)
        if (json && json !== '{}') return json
    } catch {
        // Cyclic value — fall through to the raw value for devtools.
    }
    return value
}

/**
 * Report a caught error: always to the console, and to the host-registered
 * reporter when present. `message` should be a static string (dynamic values
 * belong in `context`) so the host can group occurrences of one problem.
 * `tags` are for dimensions a host might filter/alert on (e.g. a `reason`
 * enum) — `context` alone isn't always queryable that way.
 */
export const reportSharedError = (
    message: string,
    error: unknown,
    context?: Record<string, unknown>,
    tags?: SharedErrorTags,
): void => {
    console.error(message, printable(error), ...(context ? [printable(context)] : []))
    try {
        reporter?.(message, error, context, 'error', tags)
    } catch {
        // Reporting must never break the flow it observes.
    }
}

/**
 * Expected-but-noteworthy outcomes (declined auth, degraded fallbacks): level
 * 'warning' lets the host route them to low-noise telemetry (logs/metrics)
 * instead of error issues, but they still become visible and alertable.
 */
export const reportSharedWarning = (
    message: string,
    context?: Record<string, unknown>,
    tags?: SharedErrorTags,
): void => {
    console.warn(message, ...(context ? [printable(context)] : []))
    try {
        reporter?.(message, undefined, context, 'warning', tags)
    } catch {
        // Reporting must never break the flow it observes.
    }
}
